import React from 'react'
import PropTypes from 'prop-types'
import HashAnchor from '../HashAnchor/HashAnchor'

const PrivacyPolicyUS = ({ mobile = true }) => {
  return (
    <>
      <h2>Lifesum/Movesum Privacy Policy</h2>
      <p>
        By installing our mobile application or in any way using or accessing
        our Services, you indicate you have read and understand this Privacy
        Policy and the processing of your personal data.
      </p>

      <h3>Personal data controller</h3>

      <p>Responsible for the processing of your personal data is:</p>

      <dl>
        <dt>Company name</dt>
        <dd>Lifesum AB</dd>

        <dt>Corporate identity no:</dt>
        <dd>556729-2841</dd>

        <dt>Address</dt>
        <dd>
          Repslagargatan 17B, <br />
          118 46 Stockholm, Sweden
        </dd>

        <dt>E-mail</dt>
        <dd>
          {mobile && 'contact@lifesum.com'}
          {!mobile && (
            <a href="mailto:contact@lifesum.com">contact@lifesum.com</a>
          )}
        </dd>
      </dl>

      <h3>Information about personal data we collect</h3>

      <p>
        When you register for Lifesum’s Services, we collect personal data from
        you such as: your email address, first and last name, height, weight,
        date of birth, and gender.
      </p>
      <p>
        When you continue using the Lifesum Services you regularly upload
        personal data onto your account such as, depending on your use of the
        Services: your calorie intake, weight loss goal/weight gain goal,
        activity/diet routines, your body measurements, and BMI.
      </p>
      <p>
        In an ongoing effort to improve our Services, additional personal data
        may be collected from you. In such cases, we will notify you when the
        personal data collection takes place.
      </p>

      <h3>Our use of your personal data</h3>
      <p>
        Your privacy is important to us, and we will only process your personal
        data in accordance with this Privacy Policy and relevant personal data
        regulation.
      </p>

      <h3>Data processing necessary to provide the Services</h3>
      <p>
        As a part of offering the Services to you. we process your personal data
        that you provide us with at the time of registration, such as:
      </p>
      <ul>
        <li>First and last name</li>
        <li>E-mail</li>
        <li>Date of birth</li>
        <li>Country of residence</li>
        <li>Weight</li>
        <li>Height</li>
        <li>
          Device identifiers (i.e. information on what device, IP-address, etc.
          you use to register and log on to the Services), and
        </li>
        <li>
          Technical information related to your use of your device which could
          be attributed to you (i.e. geo-location data, access history, search
          history, and information regarding your interaction with the
          application or advertisements) which is used to provide the Services
          and to allow Lifesum to market to you in accordance with this Privacy
          Policy.
        </li>
      </ul>

      <p>
        As you continue to use the Services, you will regularly provide Lifesum
        with further personal data. It follows from the nature of the Services
        that we must process such data that you upload to the Services to enable
        the Services, for example, we will process your weight data and calorie
        intake to enable the monitoring and presentation of your personal goals
        (whether it be weight loss or weight gain). This processing is a
        prerequisite for us being able to offer the Services to you.
      </p>
      <p>
        If you connect to the Services using a Third-Party Application (e.g.,
        Apple HealthKit, Facebook), Lifesum will collect and use your personal
        information from such third party applications such as your username,
        name, profile picture, country, hometown, email address, date of birth,
        and gender, as well as such health data that you choose to synchronize
        with Lifesum’s Services.
      </p>
      <p>
        For some of the data mentioned above, collected from you and processed
        by Lifesum for certain features of the Services, it may be possible for
        us to delete and cease processing for the future if you so require,
        without your having to terminate your use of the entire Services, i.e.,
        not all data (submitted over time by you to us either directly or
        through third-party services) is required for you to still be able to
        use the Services. If you want us to cease processing certain data about
        you under this category, we will inform you about the consequences of
        such cessation. Specifically, we will inform you how any deletion of
        your data will affect your use of the features of the Services.
      </p>

      <h3>Customer relationship as a legal ground</h3>
      <p>
        It should be noted that marketing sent by Lifesum to you on the basis of
        a customer relationship as a legal ground for processing personal data
        does not require a consent. Such data will be processed and used in
        compliance with relevant personal data regulation. Note that you may
        have a right to object to processing under this legal basis depending on
        the jurisdiction you live in.
      </p>

      <h3>Profiling</h3>
      <p>
        Lifesum’s Services are based on a profound knowledge of best practice
        with regard to health. Lifesum cooperates with nutritionists, behavioral
        psychologists, personal trainers, and stress experts to stay on top of
        the latest research and science. In addition, Lifesum analyzes the usage
        of the Services in order to provide you as a user with the most relevant
        features of the Services as possible with regard to your personal goals.
        In analyzing the usage of the Services, Lifesum will process certain
        data for so-called profiling. Profiling means processing of your and
        other users’ personal data to analyze or predict aspects of your use of
        the Services, behavior, and location in reaching your health goal. The
        data processed will be user habits where we will track actions taken by
        you, such as when you move between screens, press buttons, and take
        actions on the Services. The purpose of the profiling is to monitor how
        engaged users are in different features of our Services, find out ways
        to make the Service more effective (fit for purpose). and vary the
        features or content to better match your individual usage patterns and
        preferences.
      </p>
      <p>
        The profiling of your data can also be used as the basis for marketing
        towards other new potential customers on platforms other than the
        Lifesum Services, such as other social media platforms. Please note that
        this so-called look-alike process of recruiting new customers does not
        mean that you will receive any marketing. Nor does it mean that any
        other third parties will gain access to your data for any other purpose
        than for Lifesum to reach new customers, i.e. third party providers (for
        example social media) will only process your data on behalf of Lifesum
        as processors. See further under ”Transfer of data” below.
      </p>
      <p>
        Lifesum’s processing of your personal data for profiling purposes as
        described above does not require any consent as a legal ground to be
        processed. If this should be the case, i.e. if, under relevant personal
        data regulation, consent is required for the use as described above, you
        hereby give your consent to Lifesum to use your personal data as defined
        above for profiling, for the purpose of improving the Services, and for
        providing customer support.
      </p>

      <h3>Marketing - Data processing based on consent as the legal ground</h3>
      <p>
        You hereby consent to Lifesum using all your personal data for profiling
        and marketing purposes via emails, notifications, or other messages, by
        Lifesum or Lifesum’s selected business partners. Profiling will be used
        for marketing purposes to enable personalized and relevant offers to
        you, based on your preferences. See more above under “Profiling.”
      </p>
      <p>
        Marketing will only be sent to you by Lifesum; no personal data will be
        transferred to third parties for marketing purposes (other than possibly
        to service providers on assignment by Lifesum, however these providers
        do not have any rights to use the data for any other purposes than to
        what they have been assigned on behalf of Lifesum).
      </p>

      <h3>Opt-out from marketing</h3>
      <p>
        In case you do not want Lifesum to use your personal data for the
        purpose of marketing, you may at any time opt-out from receiving any
        further messages by clicking the &quot;unsubscribe&quot; link at the
        bottom of such message, or by using such opt-out features as are
        available in the app settings in the Services.
      </p>

      <HashAnchor use="h3" id="cookies">
        Cookies
      </HashAnchor>
      <p>
        Lifesum uses cookies in order to function properly. We use both session
        cookies (which expire once you close your web browser) and persistent
        cookies (which stay on your device for a set period of time or until you
        delete them).
      </p>
      <p>
        If your internet browser is set and configured to accept cookies, you
        consent to the use of cookies.
      </p>
      <p>
        A cookie is a small text file which is created by the websites you visit
        and may contain information about you as a visitor. These text files are
        stored on your computer and, for instance, keep you from having to
        remember your password. Information from cookies may be used to improve
        the user experience and/or the Services and to target marketing
        campaigns and offers to our customers.
      </p>
      <p>
        If you do not accept cookies, you can prevent cookies from being saved
        on your computer by changing the settings of your web browser. You can
        also delete cookies which have been previously stored. In such cases,
        please refer to the help section of your internet browser. If you choose
        not to accept cookies, you can still use our Services, but their
        functionality may be somewhat limited.
      </p>

      <h3>Rectification of personal data</h3>
      <p>
        You have the right to request access to and rectification or erasure of
        your personal data processed by Lifesum. If a request for rectification
        concerns data that is necessary for Lifesum to offer the Services, such
        requests will have the effect that your account will be terminated at
        Lifesum.
      </p>
      <p>
        If you wish to exercise this right, please contact us at
        https://lifesum.com/contact. An application shall be made in writing and
        must be signed by you. Alternatively, you can also rectify your personal
        data by deleting your account via the Services.
      </p>

      <h3>Transfer of data</h3>
      <p>
        Personal data collected from you may be shared with third-party
        providers of Lifesum that process personal data on behalf of Lifesum;
        such as server hosting providers, data storage providers, companies
        carrying out system and sales performance monitoring, customer support
        systems, and payment service providers. These service providers will be
        considered processors of your personal data.
      </p>
      <p>
        At times, some of your personal data by may be transferred and processed
        to processors, as described above, that are established in the United
        States or other countries (some of which will be outside the EEA).
        Lifesum will always apply safeguards to protect your personal data from
        unauthorized disclosure. We may also transfer your personal data in the
        event that our business (or part of it) is sold, for instance, as part
        of a merger or acquisition. In case of transfer of data to third parties
        that are outside of the EU or EEA and that do not offer an adequate
        level of data protection, Lifesum will apply the EU Commission’s model
        clauses and/or binding corporate rules, or similar
        instruments/mechanism, for the safeguarding of integrity and security.
      </p>
      <p>
        In no event does Lifesum transfer personal data to third parties with
        the right for such parties to use the data other than on behalf of
        Lifesum.
      </p>
      <p>
        Lifesum may further disclose your personal data in order to comply with
        a legal or regulatory obligation if we reasonably believe that this is
        required by law, regulation, or other legislation, or in order to
        protect and defend Lifesum, our business partners, or users’ rights and
        interests, subject always to relevant applicable personal data
        regulation.
      </p>

      <h3>Portability</h3>
      <p>
        You have the right to receive your personal data which you have provided
        to Lifesum in accordance with relevant personal data regulation,
        provided that such request does not adversely affect the rights and
        freedoms of others.
      </p>

      <h3>Complaints</h3>
      <p>
        If you have any questions or complaints about our processing of your
        personal data, you are welcome to contact us at
        https://lifesum.com/contact. You also have the right to lodge a
        complaint with a supervisory authority.
      </p>

      <h3>Security of the personal data we process</h3>
      <p>
        The security and integrity of your personal data are important to us. In
        addition to complying with applicable relevant personal data regulation,
        we use accepted industry standards, technologies, and procedures such as
        firewalls, security software, etc., in order to protect the integrity of
        your personal data and to prevent any unauthorized access. However, no
        system can be 100% secure, and despite our efforts, there is always a
        risk of unauthorized access to your personal data. By using our
        Services, you assume this risk. We also urge you to use a unique and
        strong password for your user account on the Lifesum services as well as
        to protect such password. You should further limit access to your
        computer and browser and log out after having used the Lifesum Services.
      </p>

      <h3>California Privacy Rights</h3>
      <p>
        If you are a resident of the State of California, the California Civil
        Code provides you with some additional rights beyond those specified
        above. California’s Civil Code section 1798.100, et seq.provides
        residents of California the “right to know,” the “right to delete,” and
        the “right to opt-out.”
      </p>

      <h4>Right to Know</h4>
      <p>
        You may request that Lifesum provide you the specific pieces of personal
        information that Lifesum has about you, categories of personal
        information we have collected about you, categories of sources from
        which the personal information is collected, categories of personal
        information that Lifesum sold or disclosed for a business purpose about
        you, categories of third parties to whom the personal information was
        sold or disclosed for a business purpose, and the business or commercial
        purpose for collecting or selling personal information. Please note that
        much of the information you can make a request for is already contained
        in this Privacy Policy.
      </p>
      <p>
        Should you wish to exercise this right, please see the section below
        which explains how to do so. If you do not have an account with Lifesum,
        we may ask for some personal information in order to verify your
        identity and your rights to the data subject to your request. This is
        because Lifesum is prohibited from disclosing specific pieces of
        personal information pursuant to a request under this section if we have
        not verified that the individual requesting the information is the
        individual to whom the information relates. As a consequence, we will
        match your identifying and authenticating information you provide as
        part of your request to the information we have in our databases to make
        sure we provide only you with your information.
      </p>

      <h4>Right to Delete</h4>
      <p>
        California law gives residents a limited right to request deletion of
        their personal information. However, this right is limited by a number
        of exceptions. Fundamentally, if Lifesum has a permissible need to
        retain personal information, we are not under an obligation to delete
        such information, even when requested. Generally, we retain personal
        information so we may complete the transaction for which the personal
        information was collected; provide a good or service requested by you,
        or reasonably anticipated within the context of our ongoing business
        relationship with you, or otherwise perform a contract between us and
        you; detect security incidents; protect against malicious, deceptive,
        fraudulent, or illegal activity, or prosecute those responsible for that
        activity; debug to identify and repair errors that impair existing
        intended unctionality of our online properties; enable solely internal
        uses that are reasonably aligned with your expectations based on your
        relationship with Lifesum; comply with a legal obligation; or otherwise
        use your personal information internally in a lawful manner that is
        compatible with the context in which you provided the information. As
        such, we generally do not accept requests to delete personal
        information. However, we will respond to requests to remove personal
        information from some of our systems as part of a request to close or
        otherwise disable a membership account. If you would like to request
        deletion of personal information, please see the section below which
        explains how to do so. If you do not have an account with Lifesum, we
        may ask for some personal information in order to verify your identity
        and your rights to the data subject to your request.
      </p>

      <h4>Right to Opt-Out of Sale</h4>
      <p>
        Lifesum does not sell your Personal Information to third parties, and as
        such, there is no need for you to opt-out of the sale of your personal
        information.
      </p>

      <h4>Designating an Authorized Agent</h4>
      <p>
        California law permits California residents to designate an agent to
        manage their rights under California law. If you would like to designate
        an agent to manage your privacy preferences, you may do so using the
        mechanisms noted below under “Exercising California Privacy Rights.”
        Note that If you do not have an account with Lifesum, we may ask for
        some personal information in order to verify your identity and your
        rights to the data subject to your request. We will also need sufficient
        personal information about your authorized agent to be able to identify
        them. As part of this process, you must have permission from your
        authorized agent to disclose their personal information to us for the
        purpose of acting as your agent.
      </p>

      <h4>Non-discrimination</h4>
      <p>
        California law does not permit Lifesum to discriminate against you
        because you exercised any your rights under this title, including, but
        not limited to, by denying you access to goods or services; charging
        different prices or rates for goods or services, including through the
        use of discounts or other benefits or imposing penalties; providing a
        different level or quality of goods or services; or suggesting that you
        will receive a different price or rate for goods or services or a
        different level or quality of goods or services.
      </p>

      <h4>Exercising California privacy rights</h4>
      <p>
        If you are a California resident and would like to exercise any of your
        rights, you may do so by going to our website at
        www.lifesum.com/californiaprivacyrights, emailing us at
        https://lifesum.com/contact, or calling +1 (800) 944 1644. You may also
        contact us directly in the Lifesum app via Me / Settings / Support and
        then clicking on the contact button in that section.
      </p>

      <h3>Children</h3>
      <p>
        If you are under age 13, you are not allowed not submit any personal
        data through our Services. Further, we encourage parents and legal
        guardians to monitor their children’s Internet use and to help us in
        enforcing our Privacy Policy by instructing their children to never
        provide any personal data through our Service without their permission.
        If you become aware that a child under the age of 13 has submitted
        personal data to us, please contact us at https://lifesum.com/contact.
      </p>

      <h3>Changes to our Privacy Policy</h3>
      <p>
        From time to time, it may become necessary to make changes to this
        Privacy Policy. Therefore, we reserve the right to update and/or modify
        this Privacy Policy at any time by giving notice to you. Please review
        this Privacy Policy frequently, and especially before you register an
        account with us. The date of the last modification is listed at the
        bottom of this Privacy Policy.
      </p>

      <h3>Contact</h3>
      <p>
        We will communicate with you by email and through notices posted on your
        mobile device. If you do not wish to receive notices on your mobile
        device, you can always turn off this service. Please be aware that if
        you unsubscribe from our mailing list, you will still receive answers to
        your questions sent to our support but you will not receive promotional
        emails.
      </p>
      <p>
        We welcome your feedback regarding our Services. If you have any
        questions or suggestions regarding our Privacy Policy, please contact us
        at https://lifesum.com/contact.
      </p>

      <p>
        <small>Last Updated: 08/11/2020</small>
      </p>
    </>
  )
}

PrivacyPolicyUS.propTypes = {
  mobile: PropTypes.bool,
}

export default PrivacyPolicyUS
